import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { assetUrl } from '../../services/assets';
import { ktx2Extend } from '../../utils/ktx2';
import { HELI_MODEL, HELI_LENGTH } from '../../city/heliTour';

// A HELI TOUR'S HELICOPTER (city/heliTour). The model is a Sketchfab download
// ("Helicopter", surveytown, CC-BY-4.0 — DOCS/WORLD/ATTRIBUTION.md) in its
// own centimetre-ish units, nose along +x, split by material into six meshes —
// two of which are the rotors: `elise` (the main rotor, French for propeller)
// and `rotor` (the tail's). What loads is an optimised build of it
// (scripts/optimize_heli.mjs: 30k triangles on desktop, 14k on a phone, KTX2
// textures, meshopt geometry — materials and node transforms kept, so the names
// and the hubs below still hold).
//
// THE KIT (once per load, shared by every helicopter): every mesh baked into one
// frame — nose to +z (the scene's facing), centred on the fuselage, skids on
// y=0, HELI_LENGTH long — and each rotor re-centred on its hub so it can spin
// in place. The hubs were measured (2026-11-01): the main rotor's is the centre
// of the smallest circle round its blades (its box is not: the blades rest at
// an angle), at model (48, 108, 44); the tail rotor's is its disc's centre, at
// (−183, 72.8, 42.7), spinning about the model's z (sideways).
//
// A HELICOPTER is that kit, posed every frame by `poseOf()` —
// { x, y, z, yaw, pitch, roll, spin } (city/heliTour padPose) — without React:
// a flight is sixty poses a second. `spin` 0…1 sets the rotors.
//
// ⚠ PAST A THIRD OF FULL SPEED THE BLADES ARE NOT DRAWN — a blur disc is.
// Khari, 2026-11-02: "there is a stutter of the helicopter and the pilot while I
// am viewing from within. It may have something to do with the propeller" — and
// "a similar stutter during takeoff". It was the propeller: three blades at
// 42 rad/s pass a point 20 times a second, and a frame rate in the 20s–40s
// samples that into a slow strobe (the wagon-wheel effect) — the blades, and
// their SHADOWS sweeping the cabin and the pilot, flickering a few times a second,
// in time with nothing but looking like a beat. On take-off the rotor winds up
// and the strobe sweeps through every slow rate on the way. A real camera smears
// a fast rotor into a disc; so does this: blades while slow enough to follow
// (boarding's idle, the wind-up's first moment, the wind-down's last), a
// translucent disc — no shadow — once they are not. The tail rotor the same.
const MAIN_HUB = new THREE.Vector3(48, 108, 44);
const TAIL_HUB = new THREE.Vector3(-183, 72.8, 42.7);
const MAIN_RPS = 42;   // rad/s at full spin
const TAIL_RPS = 110;
const MAIN_BLADES_UNTIL = 0.3;   // spin above which the main blades give way to the disc (≤0.42 rad a frame at 30fps: a fifth of the 120° between blades)
const TAIL_BLADES_UNTIL = 0.2;
const smooth = (a, b, x) => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** A rotor's blur: clear at the hub, a soft band toward the tips, faded at the rim. */
let blurTex = null;
function rotorBlurTexture() {
  if (blurTex) return blurTex;
  const c = document.createElement('canvas'); c.width = c.height = 256;
  const x = c.getContext('2d');
  const g = x.createRadialGradient(128, 128, 0, 128, 128, 128);
  g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(0.18, 'rgba(255,255,255,0.15)');
  g.addColorStop(0.7, 'rgba(255,255,255,0.55)'); g.addColorStop(0.9, 'rgba(255,255,255,0.75)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g; x.fillRect(0, 0, 256, 256);
  blurTex = new THREE.CanvasTexture(c); blurTex.colorSpace = THREE.SRGBColorSpace;
  return blurTex;
}

/**
 * A plain float32 copy of a geometry. The optimised builds store positions as
 * QUANTISED integers (meshopt: normalised int16, scaled back by the node's
 * transform); baking that transform into them would overflow the integer range
 * and crumple the model. So the kit's geometry is float first, then baked.
 */
function floatGeometry(src) {
  const g = new THREE.BufferGeometry();
  for (const [name, a] of Object.entries(src.attributes)) {
    if (!['position', 'normal', 'uv'].includes(name)) continue;
    const n = a.count; const k = a.itemSize; const out = new Float32Array(n * k);
    for (let i = 0; i < n; i += 1) for (let c = 0; c < k; c += 1) out[i * k + c] = a.getComponent(i, c);
    g.setAttribute(name, new THREE.BufferAttribute(out, k));
  }
  if (src.index) g.setIndex(src.index.clone());
  return g;
}

function buildKit(scene) {
  scene.updateMatrixWorld(true);
  const parts = [];
  scene.traverse((o) => { if (o.isMesh && o.geometry) parts.push({ g: floatGeometry(o.geometry).applyMatrix4(o.matrixWorld), m: o.material }); });
  // The fuselage's box decides the frame (the rotor disc would widen it).
  const box = new THREE.Box3();
  parts.forEach((p) => { if (!/elise|rotor/i.test(p.m?.name || '')) { p.g.computeBoundingBox(); box.union(p.g.boundingBox); } });
  const len = box.max.x - box.min.x || 1;
  const s = HELI_LENGTH / len;
  const cx = (box.min.x + box.max.x) / 2; const cz = (box.min.z + box.max.z) / 2;
  // model → kit: recentre, drop to the ground, scale, nose (+x) round to +z.
  const M = new THREE.Matrix4().makeRotationY(-Math.PI / 2)
    .multiply(new THREE.Matrix4().makeScale(s, s, s))
    .multiply(new THREE.Matrix4().makeTranslation(-cx, -box.min.y, -cz));
  const mainHub = MAIN_HUB.clone().applyMatrix4(M);
  const tailHub = TAIL_HUB.clone().applyMatrix4(M);
  const body = []; const main = []; const tail = [];
  for (const p of parts) {
    p.g.applyMatrix4(M);
    const name = p.m?.name || '';
    // Clear enough to see the city through from a seat (HeliDirector's cabin
    // view). Every material comes double-sided from the model, so the cabin
    // shows from inside.
    if (/glass/i.test(name) && p.m) { p.m = p.m.clone(); p.m.transparent = true; p.m.opacity = Math.min(p.m.opacity ?? 1, 0.22); p.m.depthWrite = false; }
    if (/elise/i.test(name)) { p.g.translate(-mainHub.x, -mainHub.y, -mainHub.z); main.push(p); }
    else if (/rotor/i.test(name)) { p.g.translate(-tailHub.x, -tailHub.y, -tailHub.z); tail.push(p); }
    else body.push(p);
    p.g.computeBoundingSphere();
  }
  const sizeBox = new THREE.Box3();
  body.forEach((p) => { p.g.computeBoundingBox(); sizeBox.union(p.g.boundingBox); });
  // The tail rotor's reach about its hub (its blur disc's radius).
  const tailR = tail.reduce((r, p) => Math.max(r, p.g.boundingSphere.center.length() + p.g.boundingSphere.radius), 0) || 0.4;
  return { body, main, tail, mainHub, tailHub, scale: s, size: sizeBox.getSize(new THREE.Vector3()), rotorR: 194 * s, tailR };
}

const kits = new WeakMap();
export function useHeliKit() {
  // KTX2 textures stay GPU-compressed (utils/ktx2); meshopt geometry is decoded by useGLTF.
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const gltf = useGLTF(assetUrl(HELI_MODEL), true, true, ktx2);
  return useMemo(() => {
    if (!kits.has(gltf.scene)) kits.set(gltf.scene, buildKit(gltf.scene));
    return kits.get(gltf.scene);
  }, [gltf.scene]);
}

export default function Helicopter({ poseOf, onFrame, children }) {
  const kit = useHeliKit();
  const g = useRef(null);
  const main = useRef(null);
  const tail = useRef(null);
  const disc = useRef(null);
  const tailDisc = useRef(null);
  const discMat = useMemo(() => new THREE.MeshBasicMaterial({ color: '#2a2b2e', map: rotorBlurTexture(), transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide }), []);
  const tailDiscMat = useMemo(() => discMat.clone(), [discMat]);

  useFrame((_, dt) => {
    const grp = g.current;
    const p = poseOf?.();
    if (!grp || !p) return;
    grp.position.set(p.x, p.y, p.z);
    // Yaw, then nose down (a helicopter dips forward to fly), then bank.
    grp.rotation.set(-(p.pitch || 0), p.yaw || 0, p.roll || 0, 'YXZ');
    const spin = Math.max(0, Math.min(1, p.spin || 0));
    const step = Math.min(dt, 0.05);
    if (main.current) { main.current.rotation.y += MAIN_RPS * spin * step; main.current.visible = spin <= MAIN_BLADES_UNTIL; }
    if (tail.current) { tail.current.rotation.x += TAIL_RPS * spin * step; tail.current.visible = spin <= TAIL_BLADES_UNTIL; }
    // The disc comes in as the blades go: faint at the hand-over, full at speed.
    const blur = smooth(MAIN_BLADES_UNTIL - 0.08, 0.6, spin);
    discMat.opacity = blur * 0.55;
    if (disc.current) disc.current.visible = blur > 0.001;
    const tblur = smooth(TAIL_BLADES_UNTIL - 0.06, 0.45, spin);
    tailDiscMat.opacity = tblur * 0.6;
    if (tailDisc.current) tailDisc.current.visible = tblur > 0.001;
    onFrame?.(grp, p);
  });

  return (
    <group ref={g}>
      {kit.body.map((p, i) => <mesh key={`b${i}`} geometry={p.g} material={p.m} castShadow receiveShadow />)}
      <group ref={main} position={kit.mainHub}>
        {kit.main.map((p, i) => <mesh key={`m${i}`} geometry={p.g} material={p.m} castShadow />)}
      </group>
      <mesh ref={disc} position={[kit.mainHub.x, kit.mainHub.y + 0.02, kit.mainHub.z]} rotation={[-Math.PI / 2, 0, 0]} material={discMat} visible={false}>
        <circleGeometry args={[kit.rotorR, 40]} />
      </mesh>
      <group ref={tail} position={kit.tailHub}>
        {kit.tail.map((p, i) => <mesh key={`t${i}`} geometry={p.g} material={p.m} />)}
      </group>
      {/* The tail rotor spins about kit x: its disc stands in the y–z plane. */}
      <mesh ref={tailDisc} position={kit.tailHub} rotation={[0, Math.PI / 2, 0]} material={tailDiscMat} visible={false}>
        <circleGeometry args={[kit.tailR, 32]} />
      </mesh>
      {/* Who is aboard, in kit space so they fly with it (HeliPassengers). */}
      {children}
    </group>
  );
}
